/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import dayjs from 'dayjs' // Npm: dayjs library.
import _ from 'underscore' // Npm: underscore.js library.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { HiArrowPath } from 'react-icons/hi2' // Npm: react-icons library.
import {
  Flex,
  Text,
  Button,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import ReportsCreditDropDownOptions from 'components/ReportsCreditDropDownOptions'


/*
 * GRAPHS
 */
import CustomerReadQuery from './__query__/index.customer.read.query'


/*
 * OBJECTS
 */
const _successFlags = ['READ_SUCCESSFUL']

const Index = () => {
  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [sortModel, setSortModel] = React.useState([])

  // Debounced search state
  const [searchQuery, setSearchQuery] = React.useState('')

  //  Debounced function 
  const _onSearchInputChange = React.useRef(_.debounce((value) => setSearchQuery(value), 800)).current

  // Extract search filter and trigger debounced update
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [_onSearchInputChange, filterModel])

  // Extract customer type filter 
  const customerType = React.useMemo(() => {
    const typeFilter = filterModel?.items?.find(item => item.field === 'type')
    return typeFilter?.value || undefined
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [_onSearchInputChange])

  const _customerReadVariables = React.useMemo(() => ({
      'customerType': customerType,
      'search': searchQuery,
      'skip': paginationModel?.pageSize * paginationModel?.page,
      'take': paginationModel?.pageSize
  }), [customerType, paginationModel?.page, paginationModel?.pageSize, searchQuery])
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)

  //  Query with debounced search and instant type filter
  const _QueryCustomerRead = useQuery(CustomerReadQuery, {
    'variables': _customerReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy,
  })

  const _rowCountRef = React.useRef(0)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': false,
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Event handler.
  const _HandleRefresh = React.useCallback(() => {
    _QueryCustomerRead.refetch()
  }, [_QueryCustomerRead.refetch])
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.NO',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'id',
      'description': 'ID of the customer.',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'Customer Name',
      'description': 'Name of customer.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'type',
      'headerName': 'Customer Type',
      'description': 'Type of customer.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'type': 'singleSelect',
      'valueOptions': _.without(Object.React.App.enums.CUSTOMER_TYPE.enums?.map(i => i?.key), 'TERMINATION') || []
    },
    {
      'field': 'activeBalance',
      'headerName': 'Active Balance',
      'description': 'Current active balance.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'type': 'number'
    },
    {
      'field': 'creditLimit',
      'headerName': 'Credit Limit',
      'description': 'Credit limit of customer.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'type': 'number'
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Date when customer was created.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'type': 'date',
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Date when customer was last updated.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'type': 'date',
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions available for customer.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <ReportsCreditDropDownOptions creditId={row?.creditId} />
      )
    }
  ], [])

  const rows = React.useMemo(() => _.compact(_QueryCustomerRead?.data?.CustomerRead?.map((r, index) => 'READ_SUCCESSFUL' === r.status ? ({
    'id': r?.id ?? `credit-report-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.Company?.displayName ?? '-',
    'type': r?.type ?? '-',
    'activeBalance': r?.Credit?.balance?.toFixed?.(5) ?? '-',
    'creditLimit': r?.overDraftLimit?.toFixed?.(5) ?? '-',
    'createdAt': new Date(r.createdAt),
    'updatedAt': r?.Credit?.updatedAt ? new Date(r.Credit.updatedAt + 'Z') : null,
    'creditId': r?.Credit?.id
  }) : null)), [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page, paginationModel.pageSize])
  const _rowCount = _.first(_QueryCustomerRead?.data?.CustomerRead)?._totalCount
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount) _rowCountRef.current = _rowCount

    return _rowCountRef.current
  }, [_rowCount])
  const cellsData = React.useMemo(() => _QueryCustomerRead?.data?.CustomerRead?.map(
    (item, __index) => _successFlags.includes(item.status) ? ({
      'S.No.': `${__index + (paginationModel.page * paginationModel.pageSize) + 1}.`,
      'Customer Name': item?.Company?.displayName ?? '-',
      'Customer Type': item?.type ?? '-',
      'Active Balance': item?.Credit?.balance ?? '-',
      'Credit Limit': item?.overDraftLimit ?? '-',
      'CreatedAt': dayjs(item.createdAt).format('YYYY-MM-DD'),
      'UpdatedAt': dayjs(item.updatedAt).format('YYYY-MM-DD')
    }) : {}
  ), [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page, paginationModel.pageSize])
  const headersData = React.useMemo(() => [
    'S.No.',
    'Customer Name',
    'Customer Type',
    'Active Balance',
    'Credit Limit',
    'CreatedAt',
    'UpdatedAt'
  ].map(i => ({ 'key': i, 'label': i })), [])
  const slots = React.useMemo(() => ({
    'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
    'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
    'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
  }), [])

  // Return component.
  return (
    <>
      <Flex className="reportsVendor base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="83vh"
          maxH="83vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "16px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center">
            <Flex flexDir="column">
              <Text as="h1" fontSize="2xl" color="brand.500">
                Customer's
              </Text>
              <Text as="p">
                All active customers will going to be shown here.
              </Text>
            </Flex>
            <Flex
              flex={0.5}
              justifyContent="flex-end"
              gap="12px"
              alignItems="center"
            >
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={_HandleRefresh}
                isDisabled={_QueryCustomerRead?.loading}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={cellsData}
                headersData={headersData}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            filterMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={slots}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QueryCustomerRead?.loading}
          />
        </Flex>
      </Flex>
    </>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORTS
 */
export default React.memo(Index)
